import type {
  ObservationEntity,
  UpdateObservationOptions,
  UpdateObservationVariables,
  DeleteObservationOptions,
} from "./observation.type";
import type {
  UseInvalidateOptions,
  UseValidateOptions,
} from "@/hooks/queries/validation";

import { useDelete, useUpdate } from "@/hooks/queries";
import {
  useInvalidateModel,
  useValidateModel,
} from "@/hooks/queries/validation";

export const useObservationUpdate = (
  observationId: number,
  options?: UpdateObservationOptions
) => {
  const invalidateKeys = options?.invalidateKeys;
  const { mutate, mutateAsync, ...queryResult } = useUpdate<
    ObservationEntity,
    UpdateObservationVariables
  >("observations", observationId, { invalidateKeys });

  const updateObservation = (variables: UpdateObservationVariables) => {
    mutate(variables);
  };

  const updateObservationAsync = async (
    variables: UpdateObservationVariables
  ) => {
    const response = await mutateAsync(variables);
    const newObservation = response.data.data;
    return newObservation;
  };

  return {
    ...queryResult,
    updateObservation,
    updateObservationAsync,
  };
};

export const useObservationDelete = (
  observationId: number,
  options?: DeleteObservationOptions
) => {
  const { mutate, isPending } = useDelete(
    "observations",
    observationId,
    options
  );

  return {
    deleteObservation: () => mutate(null),
    isPending,
  };
};

export const useValidateObservation = (
  observationId: number,
  options?: UseValidateOptions
) => {
  const { mutate, ...queryResult } = useValidateModel(
    "observations",
    observationId,
    options
  );

  const validate = () => {
    mutate(null);
  };

  return {
    ...queryResult,
    validate,
  };
};

export const useInvalidateObservation = (
  observationId: number,
  options?: UseInvalidateOptions
) => {
  const { mutate, ...queryResult } = useInvalidateModel(
    "observations",
    observationId,
    options
  );

  const invalidate = () => mutate(null);

  return {
    ...queryResult,
    invalidate,
  };
};
